import { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Gift, Link2, Plus, RefreshCw, Send, ShieldCheck, Tag, X } from 'lucide-react';
import { api } from '../services/api';
import {
  AdminDiscount,
  AdminInvoice,
  AdminOrgCommerce,
  AdminPlan,
  AdminPlanWrite,
  DiscountTerms,
  EntitlementValue,
  PlanCopy,
  PriceInterval,
} from '../types';
import { Card } from './Card';
import { Button } from './Button';
import { Badge } from './Badge';
import { Input } from './Input';
import { Select } from './Select';
import { Dialog } from './Dialog';
import { Alert } from './Alert';
import { Loader } from './Loader';

const INTERVALS: PriceInterval[] = ['monthly', 'quarterly', 'yearly'];

function eur(minor: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
}

function date(v: string | null | undefined): string {
  return v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

function message(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

function valueText(v: EntitlementValue | { type: string; value?: unknown }): string {
  if (v.type === 'unlimited') return 'unlimited';
  if (v.type === 'bool') return v.value ? 'yes' : 'no';
  return String(v.value ?? '');
}

/** Parse "12.50" (euros) into cents; empty means "not sold". */
function toCents(text: string): number | null {
  const t = text.trim().replace(',', '.');
  if (!t) return null;
  const n = Math.round(parseFloat(t) * 100);
  return Number.isFinite(n) && n >= 0 ? n : NaN;
}

const box: React.CSSProperties = {
  padding: '12px 14px',
  background: 'var(--bg-inset)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  fontSize: 'var(--text-sm)',
};

const textarea: React.CSSProperties = {
  width: '100%',
  minHeight: 120,
  padding: 10,
  background: 'var(--bg-inset)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--text-xs)',
};

// --- plans ---------------------------------------------------------------------

type PlanForm = {
  product_code: string;
  code: string;
  display_name: string;
  status: AdminPlan['status'];
  visibility: AdminPlan['visibility'];
  sort_order: string;
  fr: { name: string; tagline: string; features: string };
  en: { name: string; tagline: string; features: string };
  prices: Record<PriceInterval, string>;
  entitlements: string;
};

function formFrom(plan: AdminPlan | null, product = 'hosting'): PlanForm {
  const copy = (lang: string) => {
    const c: PlanCopy = plan?.translations?.[lang] || { name: '' };
    return { name: c.name || '', tagline: c.tagline || '', features: (c.features || []).join('\n') };
  };
  return {
    product_code: plan?.product_code || product,
    code: plan?.code || `${product}.`,
    display_name: plan?.display_name || '',
    status: plan?.status || 'active',
    visibility: plan?.visibility || 'private',
    sort_order: String(plan?.sort_order ?? 500),
    fr: copy('fr'),
    en: copy('en'),
    prices: Object.fromEntries(
      INTERVALS.map((i) => [i, plan?.prices[i] !== undefined ? (plan.prices[i]! / 100).toFixed(2) : '']),
    ) as Record<PriceInterval, string>,
    entitlements: JSON.stringify(plan?.entitlements || {}, null, 2),
  };
}

function PlanDialog({
  initial,
  editing,
  onClose,
  onSaved,
}: {
  initial: PlanForm;
  editing: string | null;
  onClose: () => void;
  onSaved: (plan: AdminPlan) => void;
}) {
  const [f, setF] = useState<PlanForm>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setError(null);
    const prices: Partial<Record<PriceInterval, number>> = {};
    for (const i of INTERVALS) {
      const c = toCents(f.prices[i]);
      if (Number.isNaN(c)) return setError(`The ${i} price is not a number`);
      if (c !== null) prices[i] = c;
    }
    let entitlements: Record<string, EntitlementValue>;
    try {
      entitlements = JSON.parse(f.entitlements || '{}');
    } catch {
      return setError('Entitlements must be valid JSON');
    }
    const copy = (c: PlanForm['fr']) => ({
      name: c.name.trim(),
      tagline: c.tagline.trim(),
      features: c.features
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean),
    });
    const payload: AdminPlanWrite = {
      display_name: f.display_name.trim(),
      status: f.status,
      visibility: f.visibility,
      sort_order: parseInt(f.sort_order, 10) || 0,
      translations: { fr: copy(f.fr), en: copy(f.en) },
      prices,
      entitlements,
    };
    setBusy(true);
    try {
      const saved = editing
        ? await api.admin.updatePlan(editing, payload)
        : await api.admin.createPlan({ ...payload, product_code: f.product_code, code: f.code.trim() });
      onSaved(saved);
    } catch (err) {
      setError(message(err, 'Could not save the plan'));
    } finally {
      setBusy(false);
    }
  };

  const lang = (l: 'fr' | 'en', label: string) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <strong style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{label}</strong>
      <Input label="Name" value={f[l].name} onChange={(e) => setF({ ...f, [l]: { ...f[l], name: e.target.value } })} />
      <Input
        label="Tagline"
        value={f[l].tagline}
        onChange={(e) => setF({ ...f, [l]: { ...f[l], tagline: e.target.value } })}
      />
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--text-xs)' }}>
        Features (one per line)
        <textarea
          style={{ ...textarea, minHeight: 90, fontFamily: 'var(--font-body)' }}
          value={f[l].features}
          onChange={(e) => setF({ ...f, [l]: { ...f[l], features: e.target.value } })}
        />
      </label>
    </div>
  );

  return (
    <Dialog
      open
      title={editing ? `Edit ${editing}` : 'New plan'}
      onClose={onClose}
      maxWidth={760}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || !f.display_name.trim() || !f.fr.name.trim() || !f.en.name.trim()}>
            {editing ? 'Save' : 'Create plan'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 'var(--text-sm)' }}>
        {error && <Alert tone="danger">{error}</Alert>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Select
            label="Product"
            value={f.product_code}
            disabled={!!editing}
            onChange={(e) =>
              setF({ ...f, product_code: e.target.value, code: f.code.replace(/^[^.]*\./, `${e.target.value}.`) })
            }
            options={[
              { value: 'hosting', label: 'Hosting' },
              { value: 'intel', label: 'Threat Intel' },
            ]}
          />
          <Input
            label="Code"
            mono
            value={f.code}
            disabled={!!editing}
            hint="Permanent, e.g. hosting.agency_2026"
            onChange={(e) => setF({ ...f, code: e.target.value.toLowerCase() })}
          />
          <Input
            label="Internal name"
            value={f.display_name}
            onChange={(e) => setF({ ...f, display_name: e.target.value })}
          />
          <Input label="Sort order" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: e.target.value })} />
          <Select
            label="Visibility"
            value={f.visibility}
            onChange={(e) => setF({ ...f, visibility: e.target.value as AdminPlan['visibility'] })}
            options={[
              { value: 'private', label: 'Private: by offer only' },
              { value: 'public', label: 'Public: on sale to everyone' },
            ]}
          />
          <Select
            label="Status"
            value={f.status}
            onChange={(e) => setF({ ...f, status: e.target.value as AdminPlan['status'] })}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'grandfathered', label: 'Grandfathered: holders keep it, no new sales' },
              { value: 'retired', label: 'Retired: holders keep it, no offers' },
            ]}
          />
        </div>
        <div>
          <strong style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
            Prices per unit, € before tax (leave empty: not sold for that period)
          </strong>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 6 }}>
            {INTERVALS.map((i) => (
              <Input
                key={i}
                label={i}
                value={f.prices[i]}
                onChange={(e) => setF({ ...f, prices: { ...f.prices, [i]: e.target.value } })}
              />
            ))}
          </div>
          {editing && (
            <div style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', marginTop: 6 }}>
              A new price applies to new purchases only. Current subscribers keep the price they agreed to.
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {lang('fr', 'Français')}
          {lang('en', 'English')}
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--text-xs)' }}>
          Entitlements (JSON: key → {'{'}"type": "int" | "bool" | "enum" | "unlimited", "value": …{'}'})
          <textarea style={textarea} value={f.entitlements} onChange={(e) => setF({ ...f, entitlements: e.target.value })} />
          {editing && (
            <span style={{ color: 'var(--warning)' }}>
              Entitlement changes reach every current holder at once.
            </span>
          )}
        </label>
      </div>
    </Dialog>
  );
}

export function PlansPanel() {
  const [plans, setPlans] = useState<AdminPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{ form: PlanForm; editing: string | null } | null>(null);
  const [product, setProduct] = useState('hosting');

  const load = useCallback(async () => {
    try {
      setPlans(await api.admin.listPlans());
    } catch (err) {
      setError(message(err, 'Could not load plans'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!plans) return error ? <Alert tone="danger">{error}</Alert> : <Loader label="Loading plans…" />;
  const shown = plans.filter((p) => p.product_code === product);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {error && <Alert tone="danger">{error}</Alert>}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ width: 220 }}>
          <Select
            label="Product"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            options={[...new Set(plans.map((p) => p.product_code))].map((c) => ({ value: c, label: c }))}
          />
        </div>
        <Button icon={<Plus size={14} />} onClick={() => setDialog({ form: formFrom(null, product), editing: null })}>
          New plan
        </Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-4)' }}>
        {shown.map((p) => (
          <Card key={p.code} padding="var(--space-5)" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{p.translations?.en?.name || p.display_name}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                  {p.code}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <Badge tone={p.visibility === 'public' ? 'success' : 'accent'}>{p.visibility}</Badge>
                {p.status !== 'active' && <Badge tone="warning">{p.status}</Badge>}
                {p.seeded && <Badge tone="neutral">seed</Badge>}
              </div>
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
              {p.is_free
                ? 'Free'
                : INTERVALS.filter((i) => p.prices[i] !== undefined)
                    .map((i) => `${eur(p.prices[i]!)} ${i}`)
                    .join(' · ') || 'No price on sale'}
              {' · '}
              {p.subscribers} holding
            </div>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
              {Object.entries(p.entitlements)
                .map(([k, v]) => `${k.split('.').slice(1).join('.')}: ${valueText(v)}`)
                .join(' · ')}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
              {!p.seeded && (
                <Button size="sm" variant="secondary" onClick={() => setDialog({ form: formFrom(p), editing: p.code })}>
                  Edit
                </Button>
              )}
              {!p.is_free && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Copy size={14} />}
                  onClick={() =>
                    setDialog({
                      form: {
                        ...formFrom(p),
                        code: `${p.code}_custom`,
                        display_name: `${p.display_name} (custom)`,
                        visibility: 'private',
                        status: 'active',
                      },
                      editing: null,
                    })
                  }
                >
                  Copy as custom
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
      {dialog && (
        <PlanDialog
          initial={dialog.form}
          editing={dialog.editing}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

// --- one organisation ------------------------------------------------------------

type Prompt =
  | { kind: 'comped' }
  | { kind: 'offer' }
  | { kind: 'override' }
  | { kind: 'comped-qty'; id: string; quantity: number }
  | { kind: 'comped-end'; id: string; label: string }
  | { kind: 'override-end'; id: string; label: string }
  | { kind: 'discount'; id: string; label: string }
  | { kind: 'discount-end'; id: string; label: string };

function discountText(d: AdminDiscount | DiscountTerms, currency = 'EUR'): string {
  const rate = d.percent_bp ? `${d.percent_bp / 100}%` : eur(d.amount_minor ?? 0, currency);
  const left = 'periods_remaining' in d ? d.periods_remaining : d.periods;
  const limits = [
    left !== null && left !== undefined ? `${left} period(s)${'periods_remaining' in d ? ' left' : ''}` : '',
    d.ends_at ? `until ${date(d.ends_at)}` : '',
  ].filter(Boolean);
  return `−${rate}${limits.length ? ` · ${limits.join(', ')}` : ''}`;
}

export function OrgCommerceDialog({ orgId, onClose }: { orgId: string | null; onClose: () => void }) {
  const [data, setData] = useState<AdminOrgCommerce | null>(null);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [busy, setBusy] = useState(false);
  // Prompt fields
  const [plan, setPlan] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [expires, setExpires] = useState('');
  const [note, setNote] = useState('');
  const [key, setKey] = useState('hosting.sites.max');
  const [vtype, setVtype] = useState<EntitlementValue['type']>('int');
  const [vtext, setVtext] = useState('');
  const [dmode, setDmode] = useState<'' | 'percent' | 'amount'>('');
  const [dvalue, setDvalue] = useState('');
  const [dperiods, setDperiods] = useState('');
  const [duntil, setDuntil] = useState('');
  const [dlabel, setDlabel] = useState('');
  const [importName, setImportName] = useState('');
  const [importExpiry, setImportExpiry] = useState('');
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [refundOf, setRefundOf] = useState<AdminInvoice | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');

  useEffect(() => {
    setData(null);
    setError(null);
    setNotice(null);
    setInvoices([]);
    if (!orgId) return;
    Promise.all([api.admin.commerce(orgId), api.admin.listPlans(), api.admin.invoices(orgId)])
      .then(([c, p, i]) => {
        setData(c);
        setPlans(p);
        setInvoices(i);
      })
      .catch((err) => setError(message(err, 'Could not load')));
  }, [orgId]);

  const refund = async () => {
    if (!refundOf || !orgId) return;
    const cents = refundAmount.trim() ? Math.round(Number(refundAmount.replace(',', '.')) * 100) : null;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.admin.refundInvoice(orgId, refundOf.id, refundReason.trim(), cents);
      setInvoices((list) => list.map((i) => (i.id === updated.id ? updated : i)));
      const note = updated.credit_notes.at(-1);
      setNotice(
        `Refunded and credited by ${note?.number}.` +
          (note?.delivery_status === 'delivered'
            ? ' The credit note was sent to the customer and transmitted.'
            : ' Its document is being delivered; the sweep retries if anything failed.'),
      );
      setRefundOf(null);
    } catch (err) {
      setError(message(err, 'The refund failed'));
    } finally {
      setBusy(false);
    }
  };

  const open = (p: Prompt) => {
    setPrompt(p);
    setReason('');
    setExpires('');
    setNote('');
    setVtext('');
    setDmode(p.kind === 'discount' ? 'percent' : '');
    setDvalue('');
    setDperiods('');
    setDuntil('');
    setDlabel('');
    setQuantity(p.kind === 'comped-qty' ? String(p.quantity) : '1');
    setPlan('');
  };

  const run = async (fn: () => Promise<AdminOrgCommerce>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      setData(await fn());
      setNotice(done);
      setPrompt(null);
    } catch (err) {
      setError(message(err, 'The action failed'));
    } finally {
      setBusy(false);
    }
  };

  const iso = (d: string) => (d ? new Date(`${d}T23:59:59`).toISOString() : null);

  const terms = (): DiscountTerms | null => {
    if (!dmode) return null;
    const n = parseFloat(dvalue.replace(',', '.'));
    return {
      percent_bp: dmode === 'percent' ? Math.round(n * 100) : null,
      amount_minor: dmode === 'amount' ? Math.round(n * 100) : null,
      periods: dperiods ? parseInt(dperiods, 10) : null,
      ends_at: iso(duntil),
    };
  };
  const termsValid =
    !dmode || (parseFloat(dvalue.replace(',', '.')) > 0 && (!!dperiods || !!duntil));

  const submit = () => {
    if (!orgId || !prompt) return;
    if (prompt.kind === 'comped')
      return run(
        () => api.admin.grantComped(orgId, { plan_code: plan, quantity: parseInt(quantity, 10) || 1, reason }),
        'Plan given. The product was told.',
      );
    if (prompt.kind === 'offer')
      return run(
        () =>
          api.admin.createOffer(orgId, {
            plan_code: plan,
            expires_at: iso(expires),
            note: note || undefined,
            discount: terms(),
          }),
        'Offer created. Send the link to a paying member of the org.',
      );
    if (prompt.kind === 'override') {
      let value: EntitlementValue['value'] = null;
      if (vtype === 'int') value = parseInt(vtext, 10);
      else if (vtype === 'bool') value = vtext === 'true';
      else if (vtype === 'enum') value = vtext;
      return run(
        () => api.admin.createOverride(orgId, { key, value: { type: vtype, value }, reason, expires_at: iso(expires) }),
        'Exception saved. The product was told.',
      );
    }
    if (prompt.kind === 'comped-qty')
      return run(
        () => api.admin.updateComped(orgId, prompt.id, { quantity: parseInt(quantity, 10) || 1, reason }),
        'Quantity changed.',
      );
    if (prompt.kind === 'comped-end')
      return run(() => api.admin.endComped(orgId, prompt.id, reason), 'Plan taken back.');
    if (prompt.kind === 'override-end')
      return run(() => api.admin.endOverride(orgId, prompt.id, reason), 'Exception ended.');
    if (prompt.kind === 'discount')
      return run(
        () => api.admin.createDiscount(orgId, prompt.id, { ...terms()!, label: dlabel || undefined, reason }),
        'Discount set. It applies from the next invoice.',
      );
    if (prompt.kind === 'discount-end')
      return run(() => api.admin.endDiscount(orgId, prompt.id, reason), 'Discount ended.');
  };

  const paidPlans = useMemo(
    () => plans.filter((p) => !p.is_free && p.status !== 'retired' && Object.keys(p.prices).length > 0),
    [plans],
  );
  const grantable = useMemo(() => plans.filter((p) => !p.is_free), [plans]);

  const needsReason = prompt && prompt.kind !== 'offer';
  const valid =
    !!prompt &&
    (!needsReason || reason.trim().length >= 3) &&
    ((prompt.kind !== 'comped' && prompt.kind !== 'offer') || !!plan) &&
    (prompt.kind !== 'override' || (key.includes('.') && (vtype === 'unlimited' || vtext !== ''))) &&
    termsValid;

  const promptTitle: Record<Prompt['kind'], string> = {
    comped: 'Give a plan (no billing)',
    offer: 'Offer a plan',
    override: 'Add an exception',
    'comped-qty': 'Change quantity',
    'comped-end': 'Take back the plan',
    'override-end': 'End the exception',
    discount: 'Discount',
    'discount-end': 'End the discount',
  };

  return (
    <Dialog open={!!orgId} title={data ? `${data.display_name}: plans & access` : 'Plans & access'} onClose={onClose} maxWidth={920}>
      {!data ? (
        error ? <Alert tone="danger">{error}</Alert> : <Loader label="Loading…" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, fontSize: 'var(--text-sm)' }}>
          {error && <Alert tone="danger">{error}</Alert>}
          {notice && !error && <Alert tone="success">{notice}</Alert>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button size="sm" icon={<Gift size={14} />} onClick={() => open({ kind: 'comped' })}>
              Give a plan
            </Button>
            <Button size="sm" variant="secondary" icon={<Tag size={14} />} onClick={() => open({ kind: 'offer' })}>
              Offer a plan
            </Button>
            <Button size="sm" variant="secondary" icon={<ShieldCheck size={14} />} onClick={() => open({ kind: 'override' })}>
              Add an exception
            </Button>
            <Button
              size="sm"
              variant="ghost"
              icon={<Send size={14} />}
              disabled={busy}
              onClick={() => run(() => api.admin.renotify(data.org_id), 'Entitlements re-sent to the products.')}
            >
              Re-send entitlements
            </Button>
          </div>

          {refundOf && (
            <div style={{ ...box, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>Refund {refundOf.number}</strong>
                <button type="button" onClick={() => setRefundOf(null)} style={{ color: 'var(--text-tertiary)' }}>
                  <X size={16} />
                </button>
              </div>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                The card is refunded and a credit note is issued. The invoice itself is never changed.
                {refundOf.livemode
                  ? ' The credit note is emailed to the customer and transmitted to the tax authority.'
                  : ' This is a test invoice: its credit note stays in Nexus.'}{' '}
                Up to {eur(refundOf.refundable_minor, refundOf.currency)} can still be credited.
              </p>
              <Input
                label="Reason (appears on the credit note)"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                disabled={busy}
              />
              <Input
                label={`Amount in ${refundOf.currency} (leave empty for the whole ${eur(refundOf.refundable_minor, refundOf.currency)})`}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                inputMode="decimal"
                mono
                disabled={busy}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button size="sm" variant="ghost" onClick={() => setRefundOf(null)} disabled={busy}>
                  Cancel
                </Button>
                <Button size="sm" onClick={refund} disabled={busy || refundReason.trim().length < 3}>
                  {busy ? 'Refunding…' : 'Refund and credit'}
                </Button>
              </div>
            </div>
          )}

          {prompt && (
            <div style={{ ...box, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong>{promptTitle[prompt.kind]}</strong>
                <button type="button" onClick={() => setPrompt(null)} style={{ color: 'var(--text-tertiary)' }}>
                  <X size={16} />
                </button>
              </div>
              {(prompt.kind === 'comped' || prompt.kind === 'offer') && (
                <Select
                  label="Plan"
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  options={[
                    { value: '', label: 'Choose…' },
                    ...(prompt.kind === 'offer' ? paidPlans : grantable).map((p) => ({
                      value: p.code,
                      label: `${p.translations?.en?.name || p.display_name} (${p.code}, ${p.visibility})`,
                    })),
                  ]}
                />
              )}
              {(prompt.kind === 'comped' || prompt.kind === 'comped-qty') && (
                <Input label="Quantity (Hosting: websites)" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
              )}
              {prompt.kind === 'override' && (
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
                  <Input label="Key" mono value={key} onChange={(e) => setKey(e.target.value)} />
                  <Select
                    label="Type"
                    value={vtype}
                    onChange={(e) => setVtype(e.target.value as EntitlementValue['type'])}
                    options={['int', 'bool', 'enum', 'unlimited'].map((x) => ({ value: x, label: x }))}
                  />
                  {vtype === 'bool' ? (
                    <Select
                      label="Value"
                      value={vtext}
                      onChange={(e) => setVtext(e.target.value)}
                      options={[
                        { value: '', label: 'Choose…' },
                        { value: 'true', label: 'yes' },
                        { value: 'false', label: 'no' },
                      ]}
                    />
                  ) : (
                    <Input
                      label="Value"
                      value={vtext}
                      disabled={vtype === 'unlimited'}
                      onChange={(e) => setVtext(e.target.value)}
                    />
                  )}
                </div>
              )}
              {(prompt.kind === 'offer' || prompt.kind === 'override') && (
                <Input
                  label="Expires (optional)"
                  type="date"
                  value={expires}
                  onChange={(e) => setExpires(e.target.value)}
                  hint={
                    prompt.kind === 'offer'
                      ? 'After this the link stops working. A plan already bought is kept.'
                      : 'The exception ends that day.'
                  }
                />
              )}
              {prompt.kind === 'offer' && (
                <Input label="Note (internal)" value={note} onChange={(e) => setNote(e.target.value)} />
              )}
              {(prompt.kind === 'offer' || prompt.kind === 'discount') && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                  <Select
                    label="Discount"
                    value={dmode}
                    onChange={(e) => setDmode(e.target.value as typeof dmode)}
                    options={[
                      ...(prompt.kind === 'offer' ? [{ value: '', label: 'None' }] : []),
                      { value: 'percent', label: 'Percentage' },
                      { value: 'amount', label: 'Amount per period (excl. VAT)' },
                    ]}
                  />
                  <Input
                    label={dmode === 'amount' ? 'Amount (EUR)' : 'Percent'}
                    value={dvalue}
                    disabled={!dmode}
                    onChange={(e) => setDvalue(e.target.value)}
                  />
                  <Input
                    label="For N periods"
                    value={dperiods}
                    disabled={!dmode}
                    onChange={(e) => setDperiods(e.target.value)}
                  />
                  <Input
                    label="Or until"
                    type="date"
                    value={duntil}
                    disabled={!dmode}
                    onChange={(e) => setDuntil(e.target.value)}
                  />
                </div>
              )}
              {prompt.kind === 'discount' && (
                <Input
                  label="Label on the invoice (optional)"
                  value={dlabel}
                  onChange={(e) => setDlabel(e.target.value)}
                  hint="Shown as “Remise <label> (rate)”. Starts with the next invoice; a percentage also reduces top-ups in a discounted period."
                />
              )}
              {needsReason && (
                <Input label="Reason (kept in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} />
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="sm"
                  variant={prompt.kind.endsWith('-end') ? 'danger' : 'primary'}
                  disabled={!valid || busy}
                  onClick={submit}
                >
                  Confirm
                </Button>
              </div>
            </div>
          )}

          <section>
            <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Subscriptions</h3>
            {data.subscriptions.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)' }}>None.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.subscriptions.map((s) => {
                  const holding = ['active', 'trialing', 'past_due', 'suspended'].includes(s.status);
                  return (
                    <div
                      key={s.id}
                      style={{ ...box, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', opacity: holding ? 1 : 0.55 }}
                    >
                      <div>
                        <strong>
                          {s.plan_name} × {s.quantity}
                        </strong>{' '}
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                          {s.plan_code}
                        </span>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 2 }}>
                          {s.comped
                            ? `Given by CYRN since ${date(s.started_at)}`
                            : `${eur(s.unit_amount_minor * s.quantity, s.currency)} ${s.interval} · ${
                                s.cancel_at_period_end ? 'ends' : 'renews'
                              } ${date(s.current_period_end)}`}
                          {s.scheduled_quantity !== null || s.scheduled_moves?.length ? ' · change planned at renewal' : ''}
                          {s.ended_at ? ` · ended ${date(s.ended_at)}` : ''}
                        </div>
                        {s.discounts?.[0]?.active && (
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--success)', marginTop: 2 }}>
                            {s.discounts[0].label ? `${s.discounts[0].label}: ` : 'Discount: '}
                            {discountText(s.discounts[0], s.currency)} · {s.discounts[0].reason}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {s.comped && <Badge tone="accent">comped</Badge>}
                        {s.plan_visibility === 'private' && <Badge tone="neutral">private plan</Badge>}
                        <Badge tone={s.status === 'active' ? 'success' : s.status === 'past_due' ? 'warning' : 'neutral'}>
                          {s.status}
                        </Badge>
                        {!s.comped && holding && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => open({ kind: 'discount', id: s.id, label: s.plan_name })}
                          >
                            Discount
                          </Button>
                        )}
                        {!s.comped && s.discounts?.[0]?.active && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => open({ kind: 'discount-end', id: s.id, label: s.plan_name })}
                          >
                            End discount
                          </Button>
                        )}
                        {s.comped && holding && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => open({ kind: 'comped-qty', id: s.id, quantity: s.quantity })}>
                              Quantity
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => open({ kind: 'comped-end', id: s.id, label: s.plan_name })}
                            >
                              End
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section>
            <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Offers</h3>
            {data.offers.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)' }}>None.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.offers.map((o) => (
                  <div key={o.id} style={{ ...box, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <strong>{o.plan_name}</strong>{' '}
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                        {o.accepted_at ? `bought ${date(o.accepted_at)}` : 'not bought yet'}
                        {o.expires_at ? ` · link until ${date(o.expires_at)}` : ''}
                        {o.revoked_at ? ` · withdrawn ${date(o.revoked_at)}` : ''}
                        {o.note ? ` · ${o.note}` : ''}
                        {o.discount ? ` · ${discountText(o.discount)}` : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <Badge tone={o.usable ? 'success' : 'neutral'}>{o.usable ? 'open' : 'closed'}</Badge>
                      {o.usable && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<Link2 size={14} />}
                            onClick={() => {
                              navigator.clipboard?.writeText(o.checkout_url);
                              setNotice('Checkout link copied.');
                            }}
                          >
                            Copy link
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => run(() => api.admin.revokeOffer(data.org_id, o.id), 'Offer withdrawn.')}
                          >
                            Withdraw
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Exceptions</h3>
            {data.overrides.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)' }}>None.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.overrides.map((o) => (
                  <div
                    key={o.id}
                    style={{ ...box, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', opacity: o.active ? 1 : 0.55 }}
                  >
                    <div>
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{o.key}</span> = <strong>{valueText(o.value)}</strong>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 2 }}>
                        {o.reason} · since {date(o.created_at)}
                        {o.expires_at ? ` · ${o.active ? 'until' : 'ended'} ${date(o.expires_at)}` : ''}
                      </div>
                    </div>
                    {o.active && (
                      <Button size="sm" variant="ghost" onClick={() => open({ kind: 'override-end', id: o.id, label: o.key })}>
                        End
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h3 style={{ fontWeight: 600, marginBottom: 8 }}>What the products see</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.entitlements.map((e) => (
                <div key={e.product} style={box}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <strong>
                      {e.product} · {e.status}
                    </strong>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      v{e.version ?? '—'} · told v{e.notified_version ?? '—'}
                      {e.version !== null && e.notified_version !== null && e.notified_version < e.version
                        ? ` · ${e.notify_attempts} failed attempt(s)`
                        : ''}
                    </span>
                  </div>
                  {e.last_notify_error && (
                    <div style={{ color: 'var(--warning)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
                      <RefreshCw size={12} /> {e.last_notify_error}
                    </div>
                  )}
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.6 }}>
                    {Object.entries(e.entitlements)
                      .map(([k, v]) => `${k.split('.').slice(1).join('.')}: ${valueText(v)}`)
                      .join(' · ') || 'nothing'}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {data.instances.length > 0 && (
            <section>
              <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Instances at the products</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.instances.map((i) => {
                  const last = i.ops[0];
                  const stuck = last && (last.status === 'failed' || (last.status === 'pending' && last.attempts > 0));
                  return (
                    <div key={i.product} style={box}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <div>
                          <strong>{i.product}</strong> · {i.status ?? 'not created'} → should be {i.desired}{' '}
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                            ({i.desired_reason}){i.external_ref ? ` · ref ${i.external_ref}` : ''}
                            {i.retain_until ? ` · data kept until ${date(i.retain_until)}` : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {!i.enabled && <Badge tone="neutral">provisioning off</Badge>}
                          {last && (
                            <Badge tone={last.status === 'succeeded' ? 'success' : last.status === 'failed' ? 'danger' : 'warning'}>
                              {last.op} {last.status}
                            </Badge>
                          )}
                          {i.enabled && (
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={<RefreshCw size={14} />}
                              disabled={busy}
                              onClick={() => run(() => api.admin.retryProvisioning(data.org_id, i.product), 'Provisioning run.')}
                            >
                              {stuck ? 'Retry' : 'Sync'}
                            </Button>
                          )}
                        </div>
                      </div>
                      {last?.last_error && (
                        <div style={{ color: 'var(--warning)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
                          {last.last_error} ({last.attempts} attempt(s))
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section>
            <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Domain names</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {data.domain_ops.map((o) => (
                <div key={o.id} style={box}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                      <strong style={{ fontFamily: 'var(--font-mono)' }}>{o.domain_name}</strong>{' '}
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                        {date(o.created_at)}
                        {o.completed_at ? ` · done ${date(o.completed_at)}` : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <Badge tone={o.status === 'succeeded' ? 'success' : o.status === 'failed' ? 'danger' : 'warning'}>
                        {o.op} {o.status}
                      </Badge>
                      {(o.status === 'pending' || (o.status === 'failed' && o.op !== 'register')) && (
                        <Button size="sm" variant="ghost" icon={<RefreshCw size={14} />} disabled={busy}
                          onClick={() => run(() => api.admin.retryDomainOp(data.org_id, o.id), 'Domain call sent again.')}>
                          Retry
                        </Button>
                      )}
                    </div>
                  </div>
                  {o.last_error && (
                    <div style={{ color: 'var(--warning)', fontSize: 'var(--text-xs)', marginTop: 4 }}>
                      {o.last_error} ({o.attempts} attempt(s))
                    </div>
                  )}
                </div>
              ))}
              <div style={{ ...box, display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <Input label="Import a domain registered before Nexus" placeholder="example.fr" mono
                  value={importName} onChange={(e) => setImportName(e.target.value)} />
                <Input label="Registry expiry" type="date" value={importExpiry}
                  onChange={(e) => setImportExpiry(e.target.value)} />
                <Button size="sm" disabled={busy || !importName.trim() || !importExpiry}
                  onClick={() => run(() => api.admin.importDomain(data.org_id, importName.trim(), `${importExpiry}T00:00:00Z`),
                    'Domain imported: renewed on schedule at no charge.')}>
                  Import
                </Button>
              </div>
            </div>
          </section>

          {invoices.length > 0 && (
            <section>
              <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Invoices</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {invoices.map((i) => (
                  <div key={i.id} style={box}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <div>
                        <strong style={{ fontFamily: 'var(--font-mono)' }}>{i.number}</strong>{' '}
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                          {eur(i.total_minor, i.currency)}
                          {i.issued_at ? ` · ${date(i.issued_at)}` : ''}
                          {i.pdp_status ? ` · PDP ${i.pdp_status}` : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {!i.livemode && <Badge tone="neutral">test</Badge>}
                        <Badge tone={i.status === 'paid' ? 'success' : i.status === 'void' ? 'neutral' : 'warning'}>
                          {i.status}
                        </Badge>
                        {i.refundable_minor > 0 && i.status !== 'void' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() => {
                              setRefundOf(i);
                              setRefundReason('');
                              setRefundAmount('');
                            }}
                          >
                            Refund
                          </Button>
                        )}
                      </div>
                    </div>
                    {i.credit_notes.map((n) => (
                      <div key={n.id} style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 4 }}>
                        {n.number} · −{eur(n.total_minor, i.currency)} · refunded {eur(n.refunded_minor, i.currency)} · {n.reason}
                        <Badge tone={n.delivery_status === 'delivered' ? 'success' : n.delivery_status === 'failed' ? 'danger' : 'warning'}>
                          {n.delivery_status}
                        </Badge>
                        {n.pdp_status && <span> · PDP {n.pdp_status}</span>}
                        {n.delivery_error && <span style={{ color: 'var(--danger)' }}> · {n.delivery_error}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.changes.length > 0 && (
            <section>
              <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Recent mid-period changes</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--text-xs)' }}>
                {data.changes.map((c) => (
                  <div key={c.id} style={{ color: 'var(--text-secondary)' }}>
                    {date(c.created_at)} · {c.kind} × {c.units} · {eur(c.amount_minor, c.currency)} · {c.status}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </Dialog>
  );
}
